/**
 * 암호화/복호화 테스트 페이지
 * Encryption/Decryption Test Page
 */

import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
  Chip,
  Stack,
  Paper
} from '@mui/material';
import {
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { encrypt2Async, decrypt2Async, testEncryption } from '../utils/crypto-utils';

const TestPage = () => {
  const [testString, setTestString] = useState('한동주_123456');
  const [encryptedResult, setEncryptedResult] = useState('');
  const [decryptedResult, setDecryptedResult] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // 암호화 테스트
  const handleEncrypt = async () => {
    try {
      setErrorMessage('');
      const result = await encrypt2Async(testString);
      setEncryptedResult(result);
      setTestStatus('success');
    } catch (error) {
      setErrorMessage(`암호화 오류: ${error}`);
      setTestStatus('error');
    }
  };

  // 복호화 테스트
  const handleDecrypt = async () => {
    try {
      setErrorMessage('');
      const result = await decrypt2Async(encryptedResult);
      setDecryptedResult(result);
      setTestStatus('success');
    } catch (error) {
      setErrorMessage(`복호화 오류: ${error}`);
      setTestStatus('error');
    }
  };

  // 전체 테스트 실행
  const runFullTest = async () => {
    try {
      setErrorMessage('');
      await testEncryption();
      setTestStatus('success');
    } catch (error) {
      setErrorMessage(`테스트 오류: ${error}`);
      setTestStatus('error');
    }
  };

  // 클립보드 복사
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // 예상 결과와 비교
  const expectedEncrypted = "xJjoVoEY8CrXcjqcXdwe3eTXiZzTWSyJvnAviTBuL08";
  const isEncryptionMatch = encryptedResult === expectedEncrypted;
  const isDecryptionMatch = decryptedResult === testString;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          🔐 암호화/복호화 테스트
        </Typography>
        <Typography variant="body1" color="text.secondary">
          나우앤테스트와의 연동을 위한 AES-256-CBC 암호화 방식 테스트
        </Typography>
      </Box>

      {/* 테스트 결과 상태 */}
      {testStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <CheckCircleIcon sx={{ mr: 1 }} />
          테스트가 성공적으로 완료되었습니다!
        </Alert>
      )}

      {testStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <ErrorIcon sx={{ mr: 1 }} />
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 입력 및 암호화 테스트 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                암호화 테스트
              </Typography>
              
              <TextField
                fullWidth
                label="테스트 문자열"
                value={testString}
                onChange={(e) => setTestString(e.target.value)}
                margin="normal"
                helperText="예: 한동주_123456"
              />

              <Button
                variant="contained"
                onClick={handleEncrypt}
                fullWidth
                sx={{ mt: 2 }}
              >
                암호화 실행
              </Button>

              {encryptedResult && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    암호화 결과:
                  </Typography>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50',
                      position: 'relative'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        wordBreak: 'break-all'
                      }}
                    >
                      {encryptedResult}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => copyToClipboard(encryptedResult)}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      복사
                    </Button>
                  </Paper>
                  
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={isEncryptionMatch ? <CheckCircleIcon /> : <ErrorIcon />}
                      label={isEncryptionMatch ? "예상 결과와 일치" : "예상 결과와 불일치"}
                      color={isEncryptionMatch ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 복호화 테스트 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                복호화 테스트
              </Typography>

              <TextField
                fullWidth
                label="암호화된 문자열"
                value={encryptedResult}
                onChange={(e) => setEncryptedResult(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                helperText="암호화된 문자열을 입력하거나 위의 암호화 결과를 사용하세요"
              />

              <Button
                variant="contained"
                onClick={handleDecrypt}
                fullWidth
                sx={{ mt: 2 }}
                disabled={!encryptedResult}
              >
                복호화 실행
              </Button>

              {decryptedResult && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    복호화 결과:
                  </Typography>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50',
                      position: 'relative'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        wordBreak: 'break-all'
                      }}
                    >
                      {decryptedResult}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => copyToClipboard(decryptedResult)}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      복사
                    </Button>
                  </Paper>
                  
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={isDecryptionMatch ? <CheckCircleIcon /> : <ErrorIcon />}
                      label={isDecryptionMatch ? "원본과 일치" : "원본과 불일치"}
                      color={isDecryptionMatch ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 전체 테스트 및 정보 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🧪 전체 테스트 및 정보
              </Typography>
              
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  onClick={runFullTest}
                  startIcon={<SecurityIcon />}
                >
                  전체 테스트 실행 (콘솔 확인)
                </Button>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    📋 테스트 정보:
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      • <strong>암호화 방식:</strong> AES-256-CBC
                    </Typography>
                    <Typography variant="body2">
                      • <strong>시크릿 키:</strong> elim_kpc_dev_2025092227873926
                    </Typography>
                    <Typography variant="body2">
                      • <strong>시크릿 IV:</strong> %^&#@$*()+=-_490
                    </Typography>
                    <Typography variant="body2">
                      • <strong>예상 암호화 결과:</strong> xJjoVoEY8CrXcjqcXdwe3eTXiZzTWSyJvnAviTBuL08
                    </Typography>
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    🔗 iframe 테스트 URL:
                  </Typography>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'grey.50',
                      position: 'relative'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        wordBreak: 'break-all'
                      }}
                    >
                      {`http://localhost:14000?user=${expectedEncrypted}`}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<CopyIcon />}
                      onClick={() => copyToClipboard(`http://localhost:14000?user=${expectedEncrypted}`)}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      복사
                    </Button>
                  </Paper>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TestPage;
